import { TableColumn, TableTooltip } from '@ui/aos-lib';
import { TranslateRegistry } from 'src/app/services/translate/translate.registry';
import { DeviceListingFieldName, EquipmentListingFieldName } from '../../equipment/devices/components/devices.models';
import { SEARCH_HINTS } from '../../../shared/components/listing-page/services/listing-page-model/listing-page-model.constants';
import { DcuIdColumnComponent } from './columns/dcu-id-column.component';
import {
  DcuMtuCommunicationTableSortName,
  DcuToMtuCommunicationFilterName,
  DcuToMtuCommunicationMeasuringPeriodValue
} from './dcu-to-mtu-communication.types';
import { tooltipConstants } from '../../../shared/constants/tooltip.consts';
import { MultiLineColumnComponent } from '@portal/shared/components/multi-line-column/multi-line-column.component';
export const FILTER_WIDTH = 160;

export const transmissionCountTooltip: TableTooltip = {
  title: TranslateRegistry.get.TOOLTIP.TITLE.TRANSMISSION_COUNT,
  text: tooltipConstants.transmissionCountTooltip,
  maxWidth: 18.3
};

export const averageRssiTooltip: TableTooltip = {
  title: TranslateRegistry.get.TOOLTIP.TITLE.AVERAGE_RSSI,
  text: tooltipConstants.averageRssiTooltip,
  maxWidth: 18.3
};

export const reliabilityTooltip: TableTooltip = {
  title: TranslateRegistry.get.TOOLTIP.TITLE.RELIABILITY,
  text: tooltipConstants.reliabilityTooltip,
  maxWidth: 18.3
};

export const tableConfig: TableColumn[] = [
  {
    field: 'mtuId',
    title: TranslateRegistry.get.MTU_TO_DCU_COMMUNICATION.TABLE.MTU_ID,
    width: 260,
    sortable: true,
    isTopAligned: true,
    link: {
      getLink: (dataItem): string => {
        return `devices/metering/${dataItem['mtuDeviceUID']}`;
      }
    }
  },
  {
    field: 'serviceLocationIdRows',
    title: TranslateRegistry.get.MTU_TO_DCU_COMMUNICATION.TABLE.SERVICE_LOCATION_NUMBER,
    width: 180,
    sortable: false,
    isTopAligned: true,
    cmp: MultiLineColumnComponent
  },
  {
    field: 'serviceLocationAddressRows',
    title: TranslateRegistry.get.MTU_TO_DCU_COMMUNICATION.TABLE.SERVICE_LOCATION_ADDRESS,
    width: 180,
    sortable: false,
    isTopAligned: true,
    cmp: MultiLineColumnComponent
  },
  {
    field: 'coverage',
    title: TranslateRegistry.get.MTU_TO_DCU_COMMUNICATION.TABLE.COVERAGE,
    width: 100,
    sortable: false,
    isTopAligned: true
  },
  {
    field: 'dcuId',
    title: TranslateRegistry.get.MTU_TO_DCU_COMMUNICATION.TABLE.DCU_ID,
    width: 260,
    sortable: false,
    isTopAligned: true,
    link: { getLink: () => 'link' },
    cmp: DcuIdColumnComponent
  },
  {
    field: 'transmissionCount',
    title: TranslateRegistry.get.MTU_TO_DCU_COMMUNICATION.TABLE.TRANSMISSION_COUNT,
    width: 200,
    sortable: false,
    isTopAligned: true,
    cmp: MultiLineColumnComponent,
    tooltip: transmissionCountTooltip
  },
  {
    field: 'averageRSSI',
    title: TranslateRegistry.get.MTU_TO_DCU_COMMUNICATION.TABLE.AVERAGE_RSSI,
    width: 115,
    sortable: false,
    isTopAligned: true,
    cmp: MultiLineColumnComponent,
    tooltip: averageRssiTooltip
  },
  {
    field: 'reliability',
    title: TranslateRegistry.get.MTU_TO_DCU_COMMUNICATION.TABLE.RELIABILITY,
    width: 120,
    sortable: false,
    isTopAligned: true,
    cmp: MultiLineColumnComponent,
    tooltip: reliabilityTooltip
  }
];

export const enum DcuToMtuCommunicationTabValue {
  Table = 'table',
  Map = 'map'
}

export const DCU_TO_MTU_COMMUNICATION_SEARCH_TYPES = [
  {
    text: TranslateRegistry.get.SEARCH.TYPE.ALL,
    value: 1,
    types: [
      DeviceListingFieldName.DcuId,
      DeviceListingFieldName.MtuId,
      DeviceListingFieldName.ServiceLocationAddress,
      DeviceListingFieldName.ServiceLocationId
    ],
    hintText: SEARCH_HINTS.Search
  },
  {
    text: TranslateRegistry.get.SEARCH.TYPE.DCU_ID,
    value: 2,
    types: [DeviceListingFieldName.DcuId],
    hintText: SEARCH_HINTS.DcuId
  },
  {
    text: TranslateRegistry.get.SEARCH.TYPE.MTU_ID,
    value: 3,
    types: [DeviceListingFieldName.MtuId],
    hintText: SEARCH_HINTS.MtuId
  },
  {
    text: TranslateRegistry.get.SEARCH.TYPE.SERVICE_LOCATION_ADDRESS,
    value: 4,
    types: [DeviceListingFieldName.ServiceLocationAddress],
    hintText: SEARCH_HINTS.ServiceLocationAddress
  },
  {
    text: TranslateRegistry.get.SEARCH.TYPE.SERVICE_LOCATION_NUMBER,
    value: 5,
    types: [DeviceListingFieldName.ServiceLocationId],
    hintText: SEARCH_HINTS.ServiceLocationNumber
  }
];

export const DEFAULT_DCU_TO_MTU_COMMUNICATION_SEARCH_TYPES = DCU_TO_MTU_COMMUNICATION_SEARCH_TYPES[0];

export const dcuMtuCommunicationSortFiled: Record<string, EquipmentListingFieldName> = {
  mtuId: DcuMtuCommunicationTableSortName.MtuId
};

export const REPORT_GROUP_FILTER = {
  title: TranslateRegistry.get.FILTER.TITLE.REPORT_GROUP,
  items: [],
  filterType: DcuToMtuCommunicationFilterName.ReportGroupUID,
  selected: [],
  isMultiselect: true,
  hasSearch: true,
  width: FILTER_WIDTH
};

export const DCU_COVERAGE_FILTER = {
  title: TranslateRegistry.get.FILTER.TITLE.DCU_COVERAGE,
  items: [
    { text: TranslateRegistry.get.FILTER.DCU_COVERAGE.COVERED_BY_0, value: '0' },
    { text: TranslateRegistry.get.FILTER.DCU_COVERAGE.COVERED_BY_1, value: '1' },
    { text: TranslateRegistry.get.FILTER.DCU_COVERAGE.COVERED_BY_2, value: '2' },
    { text: TranslateRegistry.get.FILTER.DCU_COVERAGE.COVERED_BY_3, value: '3' },
    { text: TranslateRegistry.get.FILTER.DCU_COVERAGE.COVERED_BY_4, value: '4' }
  ],
  filterType: DcuToMtuCommunicationFilterName.DcuCoverage,
  selected: [
    { text: TranslateRegistry.get.FILTER.DCU_COVERAGE.COVERED_BY_0, value: '0' },
    { text: TranslateRegistry.get.FILTER.DCU_COVERAGE.COVERED_BY_1, value: '1' },
    { text: TranslateRegistry.get.FILTER.DCU_COVERAGE.COVERED_BY_2, value: '2' },
    { text: TranslateRegistry.get.FILTER.DCU_COVERAGE.COVERED_BY_3, value: '3' },
    { text: TranslateRegistry.get.FILTER.DCU_COVERAGE.COVERED_BY_4, value: '4' }
  ],
  isMultiselect: true,
  width: FILTER_WIDTH
};

export const DATE_RANGE_FILTER = {
  title: TranslateRegistry.get.FILTER.TITLE.DATE_RANGE,
  items: [
    {
      text: TranslateRegistry.get.FILTER.DATE_RANGE.YESTERDAY,
      value: DcuToMtuCommunicationMeasuringPeriodValue.YESTERDAY
    },
    {
      text: TranslateRegistry.get.FILTER.DATE_RANGE.LAST_3_DAYS,
      value: DcuToMtuCommunicationMeasuringPeriodValue.LAST_3_DAYS
    },
    {
      text: TranslateRegistry.get.FILTER.DATE_RANGE.LAST_7_DAYS,
      value: DcuToMtuCommunicationMeasuringPeriodValue.LAST_7_DAYS
    }
  ],
  filterType: DcuToMtuCommunicationFilterName.DateRange,
  selected: [
    {
      text: TranslateRegistry.get.FILTER.DATE_RANGE.LAST_7_DAYS,
      value: DcuToMtuCommunicationMeasuringPeriodValue.LAST_7_DAYS
    }
  ],
  isMultiselect: false,
  width: FILTER_WIDTH
};

export const RELIABILITY_FILTER = {
  title: TranslateRegistry.get.FILTER.TITLE.RELIABILITY,
  items: [
    { text: TranslateRegistry.get.FILTER.RELIABILITY.ALL, value: '' },
    { text: TranslateRegistry.get.FILTER.RELIABILITY['90_PERCENT'], value: '90' },
    { text: TranslateRegistry.get.FILTER.RELIABILITY['80_PERCENT'], value: '80' },
    { text: TranslateRegistry.get.FILTER.RELIABILITY['70_PERCENT'], value: '70' },
    { text: TranslateRegistry.get.FILTER.RELIABILITY['60_PERCENT'], value: '60' },
    { text: TranslateRegistry.get.FILTER.RELIABILITY['50_PERCENT'], value: '50' }
  ],
  filterType: DcuToMtuCommunicationFilterName.Reliability,
  selected: [{ text: TranslateRegistry.get.FILTER.RELIABILITY.ALL, value: '' }],
  isMultiselect: false,
  width: FILTER_WIDTH
};
